import { List, Show, Create, Edit, Datagrid, TextField, DateField, ChipField, SimpleForm, TextInput, SelectInput, Button, useGetList, type RaRecord } from 'react-admin';
import { useState } from 'react';
import { Box, Typography, Paper, Grid, LinearProgress, Alert, Card, CardContent, Chip, List as MuiList, ListItem, ListItemIcon, ListItemText, Stepper, Step, StepLabel, Avatar, Table, TableBody, TableCell, TableHead, TableRow, TextField as MuiTextField, InputAdornment, Select as MuiSelect, MenuItem, FormControl, InputLabel, useMediaQuery, TablePagination, IconButton } from '@mui/material';
import type { ChipColor, ProgressColor } from '@/types/chip';
import { Link, useNavigate } from 'react-router-dom';
import { Security, FactCheck, PrivacyTip, Schedule, CheckCircle, Warning, Error as ErrorIcon, TrendingUp, Visibility, Rule, AccountTree, Search as SearchIcon, Download as DownloadIcon, Person, Close } from '@mui/icons-material';
import { BarChart, PieChart } from '@mui/x-charts-pro';
import { ListPageHeader } from '@/components/ListPageHeader';

const AUDIT_ACTIONS = ['Create', 'Update', 'Delete', 'Login', 'Logout', 'Export', 'View'];
const DSAR_TYPES = ['Access', 'Correction', 'Deletion', 'Portability'];
const DSAR_STATUS = ['Received', 'Processing', 'Completed', 'Denied'];
const COMPLIANCE_FREQUENCY = ['Monthly', 'Quarterly', 'Annual', 'OneTime'];
const AUDIT_SEVERITY = ['Info', 'Warning', 'Critical'];
const STATUS_COLORS: Record<string, string> = {
  Received: 'warning',
  Processing: 'info',
  Completed: 'success',
  Denied: 'error',
};
const ACTION_COLORS: Record<string, string> = {
  Create: 'success',
  Update: 'info',
  Delete: 'error',
  Login: 'default',
  Logout: 'default',
  Export: 'default',
  View: 'default',
};

interface AuditRecord {
  id?: string;
  action?: string;
  actor?: { id?: string; name?: string };
  resource?: { id?: string; type?: string };
  timestamp?: string;
  [key: string]: unknown;
}

interface CalendarRecord {
  id?: string;
  status?: string;
  title?: string;
  frequency?: string;
  dueDate?: string;
  completedAt?: string;
  assignedTo?: string;
  submissionType?: string;
  legislation?: string;
  [key: string]: unknown;
}

interface DSARRecord {
  id?: string;
  status?: string;
  requestType?: string;
  requesterName?: string;
  email?: string;
  receivedDate?: string;
  completedDate?: string;
  reference?: string;
  subjectName?: string;
  type?: string;
  requestedAt?: string;
  responseDate?: string;
  description?: string;
  [key: string]: unknown;
}

export const AuditList = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('all');
  const { data: logs, isLoading } = useGetList('audit-logs', { pagination: { page: page + 1, perPage: rowsPerPage } });
  const isMobile = useMediaQuery((theme: { breakpoints: { down: (key: string) => string } }) => theme.breakpoints.down('md'));

  const stats = {
    total: logs?.length ?? 0,
    create: logs?.filter((l: AuditRecord) => l.action === 'Create').length ?? 0,
    update: logs?.filter((l: AuditRecord) => l.action === 'Update').length ?? 0,
    delete: logs?.filter((l: AuditRecord) => l.action === 'Delete').length ?? 0,
  };

  const filtered = (logs || []).filter((e: AuditRecord) => {
    const matchesSearch = (e.actor?.name?.toLowerCase() || e.actor?.id?.toLowerCase() || '').includes(search.toLowerCase()) || (e.resource?.id?.toLowerCase() || '').includes(search.toLowerCase()) || (e.resource?.type?.toLowerCase() || '').includes(search.toLowerCase());
    const matchesFilter = actionFilter === 'all' || (e.action?.toLowerCase() || '') === actionFilter.toLowerCase();
    return matchesSearch && matchesFilter;
  });

  if (isMobile) {
    return (
      <Box sx={{ p: 2 }}>
        <ListPageHeader
          title="Audit Logs"
          metrics={[
            { label: 'Creates', value: String(stats.create), color: 'success.main' },
            { label: 'Updates', value: String(stats.update), color: 'info.main' },
            { label: 'Deletes', value: String(stats.delete), color: 'error.main' },
          ]}
        />
        <Box sx={{ mb: 2, mt: 2 }}>
          <MuiTextField size="small" placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} fullWidth />
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {filtered.map((entry: AuditRecord) => (
            <Paper key={entry.id} sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar sx={{ width: 32, height: 32, bgcolor: `${ACTION_COLORS[entry.action ?? ''] || 'default'}.main`, fontSize: 12 }}>
                    <Security sx={{ fontSize: 16 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="body2" fontWeight={500}>{entry.actor?.name || entry.actor?.id || 'System'}</Typography>
                    <Typography variant="caption" color="text.secondary">{entry.resource?.type} · {entry.resource?.id}</Typography>
                  </Box>
                </Box>
                <Chip label={entry.action} size="small" color={ACTION_COLORS[entry.action ?? ''] as ChipColor} />
              </Box>
              <Typography variant="caption" color="text.secondary">{entry.timestamp}</Typography>
            </Paper>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <ListPageHeader
        title="Audit Logs"
        metrics={[
          { label: 'Creates', value: String(stats.create), color: 'success.main' },
          { label: 'Updates', value: String(stats.update), color: 'info.main' },
          { label: 'Deletes', value: String(stats.delete), color: 'error.main' },
          { label: 'Total', value: String(stats.total), color: 'primary.main' },
        ]}
      />
      <Box sx={{ display: 'flex', gap: 2, mb: 2, mt: 2 }}>
        <MuiTextField size="small" placeholder="Search by user, entity or ID..." value={search} onChange={(e) => setSearch(e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} sx={{ width: 300 }} />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Action</InputLabel>
          <MuiSelect value={actionFilter} label="Action" onChange={(e) => setActionFilter(e.target.value)}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="Create">Create</MenuItem>
            <MenuItem value="Update">Update</MenuItem>
            <MenuItem value="Delete">Delete</MenuItem>
            <MenuItem value="Login">Login</MenuItem>
            <MenuItem value="Logout">Logout</MenuItem>
            <MenuItem value="Export">Export</MenuItem>
            <MenuItem value="View">View</MenuItem>
          </MuiSelect>
        </FormControl>
        <Box sx={{ flex: 1 }} />
        <Button variant="outlined" startIcon={<DownloadIcon />}>Export</Button>
      </Box>

      <Paper sx={{ overflow: 'hidden' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 600 }}>Timestamp</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Entity Type</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Entity ID</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((entry: AuditRecord) => (
              <TableRow key={entry.id} hover>
                <TableCell>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{entry.timestamp}</Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 24, height: 24, bgcolor: 'grey.300', fontSize: 10 }}>
                      <Person sx={{ fontSize: 14 }} />
                    </Avatar>
                    <Typography variant="body2">{entry.actor?.name || entry.actor?.id || 'System'}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip label={entry.action} color={ACTION_COLORS[entry.action ?? ''] as ChipColor} size="small" />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{entry.resource?.type}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{entry.resource?.id}</Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={logs?.length ?? 0}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{ borderTop: '1px solid', borderColor: 'divider' }}
        />
        {(!logs || logs.length === 0) && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Security sx={{ fontSize: 48, color: 'grey.300', mb: 1 }} />
            <Typography color="text.secondary">No audit logs found</Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

const AuditShowContent = ({ record }: { record?: RaRecord }) => {
  const navigate = useNavigate();
  if (!record) return null;
  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <IconButton onClick={() => navigate('/audit-logs')} size="small" sx={{ color: 'text.secondary' }}>
          <Close />
        </IconButton>
      </Box>
      <Alert severity="info" sx={{ mb: 2 }} icon={<Security />}>
        Audit log — immutable record per POPIA requirements
      </Alert>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Timestamp', value: record.timestamp, bold: true },
          { label: 'Actor', value: record.actor?.name || record.actor?.id || 'System' },
          { label: 'Action', value: record.action, chip: true },
          { label: 'Resource Type', value: record.resource?.type },
          { label: 'Resource ID', value: record.resource?.id },
        ].map(f => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={f.label}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">{f.label}</Typography>
              {f.chip ? (
                <Box sx={{ mt: 0.5 }}>
                  <Chip label={f.value} size="small" />
                </Box>
              ) : (
                <Typography variant="body1" sx={{ fontWeight: f.bold ? 700 : 400, mt: 0.5 }}>{f.value ?? '—'}</Typography>
              )}
            </Paper>
          </Grid>
        ))}
      </Grid>

      {record.details && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.900', borderLeft: '4px solid', borderColor: 'info.main' }}>
          <Typography variant="caption" sx={{ color: 'grey.400', fontWeight: 600 }}>Changes (Audit Trail)</Typography>
          <Typography variant="body2" sx={{ color: 'grey.300', mt: 0.5, fontFamily: 'monospace' }}>{JSON.stringify(record.details)}</Typography>
        </Paper>
      )}

      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button component={Link} to="/compliance-calendar" variant="outlined" size="small" startIcon={<Schedule />}>
          Compliance Calendar
        </Button>
        <Button component={Link} to="/dsar-requests" variant="outlined" size="small" startIcon={<PrivacyTip />}>
          DSAR Requests
        </Button>
      </Box>
    </Box>
  );
};

export const AuditShow = () => (
  <Show render={(props) => <AuditShowContent {...props} />} />
);

export const AuditCreate = () => (
  <Create>
    <SimpleForm>
      <Alert severity="info" sx={{ mb: 2 }}>
        Audit logs are auto-generated by the system — manual creation is not typical
      </Alert>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
        <TextInput source="actor.name" label="Actor Name" fullWidth />
        <SelectInput source="action" choices={AUDIT_ACTIONS.map(a => ({ id: a, name: a }))} fullWidth />
        <TextInput source="resourceType" label="Resource Type" fullWidth />
        <TextInput source="resourceId" label="Resource ID" fullWidth />
      </Box>
      <TextInput source="details" multiline fullWidth />
    </SimpleForm>
  </Create>
);

export const AuditEdit = () => (
  <Edit>
    <SimpleForm>
      <Alert severity="warning" sx={{ mb: 2 }}>
        Audit logs are immutable — editing is restricted
      </Alert>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
        <TextInput source="actor.name" label="Actor Name" fullWidth />
        <SelectInput source="action" choices={AUDIT_ACTIONS.map(a => ({ id: a, name: a }))} fullWidth />
        <TextInput source="resourceType" label="Resource Type" fullWidth />
        <TextInput source="resourceId" label="Resource ID" fullWidth />
      </Box>
      <TextInput source="details" multiline fullWidth />
    </SimpleForm>
  </Edit>
);

const CALENDAR_STATUS_COLORS: Record<string, string> = {
  Pending: 'warning',
  Completed: 'success',
  Overdue: 'error',
};

const ComplianceCalendarSparkline = ({ dueDate, status }: { dueDate: string; status: string }) => {
  const [now] = useState(() => Date.now());

  if (status === 'Completed') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CheckCircle sx={{ fontSize: 14, color: 'success.main' }} />
        <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 600 }}>Completed</Typography>
      </Box>
    );
  }

  if (status === 'Overdue') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Warning sx={{ fontSize: 14, color: 'error.main' }} />
        <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 600 }}>Overdue</Typography>
      </Box>
    );
  }

  const due = new Date(dueDate).getTime();
  const daysRemaining = Math.ceil((due - now) / (1000 * 60 * 60 * 24));

  const getColor = () => {
    if (daysRemaining <= 0) return 'error';
    if (daysRemaining <= 7) return 'error';
    if (daysRemaining <= 30) return 'warning';
    return 'success';
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 100 }}>
      <Typography variant="caption" sx={{ color: `${getColor()}.main`, fontWeight: 600 }}>
        {daysRemaining <= 0 ? 'Overdue' : `${daysRemaining}d`}
      </Typography>
    </Box>
  );
};

export const ComplianceCalendarList = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const { data: calendarItems, isLoading } = useGetList('compliance-calendar', { pagination: { page: page + 1, perPage: rowsPerPage } });
  const isMobile = useMediaQuery((theme: { breakpoints: { down: (key: string) => string } }) => theme.breakpoints.down('md'));

  const [now] = useState(() => Date.now());
  const stats = {
    total: calendarItems?.length ?? 0,
    pending: calendarItems?.filter((c: CalendarRecord) => c.status === 'Pending').length ?? 0,
    overdue: calendarItems?.filter((c: CalendarRecord) => {
      const due = new Date(c.dueDate ?? '').getTime();
      return due < now && c.status !== 'Completed';
    }).length ?? 0,
    completed: calendarItems?.filter((c: CalendarRecord) => c.status === 'Completed').length ?? 0,
  };

  const frequencyCounts = calendarItems?.reduce((acc: Record<string, number>, c: CalendarRecord) => {
    const freq = c.frequency ?? '';
    acc[freq] = (acc[freq] || 0) + 1;
    return acc;
  }, {}) ?? {};

  const pieData = Object.entries(frequencyCounts).map(([label, value]) => ({ label, value: value as number }));

  if (isMobile) {
    return (
      <Box sx={{ p: 2 }}>
        <ListPageHeader
          title="Compliance Calendar"
          metrics={[
            { label: 'Pending', value: String(stats.pending), color: 'warning.main' },
            { label: 'Overdue', value: String(stats.overdue), color: 'error.main' },
            { label: 'Completed', value: String(stats.completed), color: 'success.main' },
          ]}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
          {calendarItems?.map((item: CalendarRecord) => {
            const isOverdue = new Date(item.dueDate ?? '').getTime() < now && item.status !== 'Completed';
            return (
              <Paper key={item.id} sx={{ p: 2 }} onClick={() => navigate(`/compliance-calendar/${item.id}/show`)}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>{item.submissionType}</Typography>
                    <Typography variant="caption" color="text.secondary">{item.legislation}</Typography>
                  </Box>
                  <Chip label={item.status} size="small" color={CALENDAR_STATUS_COLORS[item.status ?? ''] as ChipColor} />
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Typography variant="caption" color="text.secondary">Due: {item.dueDate}</Typography>
                  <Typography variant="caption" color="text.secondary">·</Typography>
                  <Typography variant="caption" color="text.secondary">{item.frequency}</Typography>
                </Box>
              </Paper>
            );
          })}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <ListPageHeader
        title="Compliance Calendar"
        metrics={[
          { label: 'Pending', value: String(stats.pending), color: 'warning.main' },
          { label: 'Overdue', value: String(stats.overdue), color: 'error.main' },
          { label: 'Completed', value: String(stats.completed), color: 'success.main' },
          { label: 'Total', value: String(stats.total), color: 'primary.main' },
        ]}
        pieChartData={pieData}
        pieChartTitle="By Frequency"
      />
      <Paper sx={{ mt: 2, overflow: 'hidden' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 600 }}>Submission</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Legislation</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Frequency</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Due Date</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {calendarItems?.map((item: CalendarRecord) => {
              const isOverdue = new Date(item.dueDate ?? '').getTime() < now && item.status !== 'Completed';
              return (
                <TableRow
                  key={item.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/compliance-calendar/${item.id}/show`)}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>{item.submissionType}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">{item.legislation}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={item.frequency} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color={isOverdue ? 'error.main' : 'text.primary'}>{item.dueDate}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={item.status} size="small" color={CALENDAR_STATUS_COLORS[item.status ?? ''] as ChipColor} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={calendarItems?.length ?? 0}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{ borderTop: '1px solid', borderColor: 'divider' }}
        />
        {(!calendarItems || calendarItems.length === 0) && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Schedule sx={{ fontSize: 48, color: 'grey.300', mb: 1 }} />
            <Typography color="text.secondary">No compliance items found</Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

const ComplianceCalendarShowContent = ({ record }: { record?: RaRecord }) => {
  const navigate = useNavigate();
  const [now] = useState(() => Date.now());
  if (!record) return null;
  const isOverdue = record.dueDate && new Date(record.dueDate).getTime() < now;
  const daysUntilDue = record.dueDate ? Math.ceil((new Date(record.dueDate).getTime() - now) / (1000 * 60 * 60 * 24)) : null;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <IconButton onClick={() => navigate('/compliance-calendar')} size="small" sx={{ color: 'text.secondary' }}>
          <Close />
        </IconButton>
      </Box>
      {isOverdue && (
        <Alert severity="error" sx={{ mb: 2 }} icon={<Warning />}>
          Overdue submission — immediate action required
        </Alert>
      )}
      {daysUntilDue !== null && daysUntilDue <= 7 && !isOverdue && (
        <Alert severity="warning" sx={{ mb: 2 }} icon={<Schedule />}>
          Due in {daysUntilDue} days — prepare submission
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Submission Type', value: record.submissionType, bold: true },
          { label: 'Legislation', value: record.legislation },
          { label: 'Frequency', value: record.frequency, chip: true },
          { label: 'Due Date', value: record.dueDate, warning: isOverdue },
          { label: 'Status', value: record.status, chip: true },
          { label: 'Days Until Due', value: daysUntilDue !== null ? `${daysUntilDue}d` : '—', highlight: daysUntilDue !== null && daysUntilDue <= 7 },
        ].map(f => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={f.label}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">{f.label}</Typography>
              {f.chip ? (
                <Box sx={{ mt: 0.5 }}>
                  <Chip label={f.value} size="small" />
                </Box>
              ) : (
                <Typography variant="body1" sx={{ fontWeight: f.bold ? 700 : 400, mt: 0.5, color: (f as { warning?: boolean; highlight?: boolean }).warning ? 'error.main' : (f as { warning?: boolean; highlight?: boolean }).highlight ? 'warning.main' : 'inherit' }}>{f.value ?? '—'}</Typography>
              )}
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>Upcoming Deadlines</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {[
                { name: 'SDL Return', due: '2026-06-10', days: 11 },
                { name: 'UIF Return', due: '2026-06-25', days: 26 },
                { name: 'EEA2 Report', due: '2026-09-30', days: 93 },
                { name: 'WSP/ATR', due: '2027-03-31', days: 305 },
              ].map(item => (
                <Box key={item.name} sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1.5, bgcolor: item.days <= 14 ? 'warning.light' : 'action.hover', borderRadius: 1 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{item.name}</Typography>
                    <Typography variant="caption" color="text.secondary">Due: {item.due}</Typography>
                  </Box>
                  <Chip label={`${item.days}d`} size="small" color={item.days <= 7 ? 'error' : item.days <= 14 ? 'warning' : 'default'} />
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>POPIA Compliance Status</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {[
                { item: 'Data inventory updated', status: true },
                { item: 'POPIA training completed', status: true },
                { item: 'Retention schedules defined', status: true },
                { item: 'Deletion queue processed', status: false },
                { item: 'DSAR procedures documented', status: true },
              ].map((check, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {check.status ? <CheckCircle color="success" fontSize="small" /> : <ErrorIcon color="warning" fontSize="small" />}
                  <Typography variant="body2">{check.item}</Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button component={Link} to="/audit-logs" variant="outlined" size="small" startIcon={<FactCheck />}>
          Audit Logs
        </Button>
        <Button component={Link} to="/dsar-requests" variant="outlined" size="small" startIcon={<PrivacyTip />}>
          DSAR Requests
        </Button>
      </Box>
    </Box>
  );
};

export const ComplianceCalendarShow = () => (
  <Show render={(props) => <ComplianceCalendarShowContent {...props} />} />
);

export const ComplianceCalendarCreate = () => (
  <Create>
    <SimpleForm>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
        <TextInput source="submissionType" fullWidth />
        <TextInput source="legislation" fullWidth />
        <SelectInput source="frequency" choices={COMPLIANCE_FREQUENCY.map(f => ({ id: f, name: f }))} fullWidth />
        <DateField source="dueDate" />
        <SelectInput source="status" choices={['Pending', 'Completed', 'Overdue'].map(s => ({ id: s, name: s }))} fullWidth defaultValue="Pending" />
      </Box>
    </SimpleForm>
  </Create>
);

export const ComplianceCalendarEdit = () => (
  <Edit>
    <SimpleForm>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
        <TextInput source="submissionType" fullWidth />
        <TextInput source="legislation" fullWidth />
        <SelectInput source="frequency" choices={COMPLIANCE_FREQUENCY.map(f => ({ id: f, name: f }))} fullWidth />
        <DateField source="dueDate" />
        <SelectInput source="status" choices={['Pending', 'Completed', 'Overdue'].map(s => ({ id: s, name: s }))} fullWidth />
      </Box>
    </SimpleForm>
  </Edit>
);

const DSAR_TYPE_COLORS: Record<string, string> = {
  Access: 'info',
  Correction: 'warning',
  Deletion: 'error',
  Portability: 'success',
};

const DSARSparkline = ({ requestedAt, status }: { requestedAt: string; status: string }) => {
  const [now] = useState(() => Date.now());

  if (status === 'Completed' || status === 'Denied') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CheckCircle sx={{ fontSize: 14, color: 'success.main' }} />
        <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 600 }}>Done</Typography>
      </Box>
    );
  }

  const requested = new Date(requestedAt).getTime();
  const daysElapsed = Math.floor((now - requested) / (1000 * 60 * 60 * 24));
  const daysRemaining = 30 - daysElapsed;

  const getColor = () => {
    if (daysRemaining <= 0) return 'error';
    if (daysRemaining <= 7) return 'error';
    if (daysRemaining <= 14) return 'warning';
    return 'success';
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 100 }}>
      <Typography variant="caption" sx={{ color: `${getColor()}.main`, fontWeight: 600 }}>
        {daysRemaining <= 0 ? 'Overdue' : `${daysRemaining}d left`}
      </Typography>
    </Box>
  );
};

export const DSARRequestList = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const { data: dsarItems, isLoading } = useGetList('dsar-requests', { pagination: { page: page + 1, perPage: rowsPerPage } });
  const isMobile = useMediaQuery((theme: { breakpoints: { down: (key: string) => string } }) => theme.breakpoints.down('md'));

  const stats = {
    total: dsarItems?.length ?? 0,
    pending: dsarItems?.filter((d: DSARRecord) => d.status === 'Received').length ?? 0,
    inProgress: dsarItems?.filter((d: DSARRecord) => d.status === 'Processing').length ?? 0,
    completed: dsarItems?.filter((d: DSARRecord) => d.status === 'Completed').length ?? 0,
  };

  const typeCounts = dsarItems?.reduce((acc: Record<string, number>, d: DSARRecord) => {
    const t = d.type ?? '';
    acc[t] = (acc[t] || 0) + 1;
    return acc;
  }, {}) ?? {};

  const pieData = Object.entries(typeCounts).map(([label, value]) => ({ label, value: value as number }));

  if (isMobile) {
    return (
      <Box sx={{ p: 2 }}>
        <ListPageHeader
          title="DSAR Requests"
          metrics={[
            { label: 'Received', value: String(stats.pending), color: 'warning.main' },
            { label: 'Processing', value: String(stats.inProgress), color: 'info.main' },
            { label: 'Completed', value: String(stats.completed), color: 'success.main' },
          ]}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
          {dsarItems?.map((item: DSARRecord) => (
            <Paper key={item.id} sx={{ p: 2 }} onClick={() => navigate(`/dsar-requests/${item.id}/show`)}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">{item.reference}</Typography>
                  <Typography variant="subtitle2" fontWeight={600}>{item.subjectName}</Typography>
                </Box>
                <Chip label={item.status} size="small" color={STATUS_COLORS[item.status ?? ''] as ChipColor} />
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Chip label={item.type} size="small" color={DSAR_TYPE_COLORS[item.type ?? ''] as ChipColor} />
                <Typography variant="caption" color="text.secondary">Requested: {item.requestedAt}</Typography>
              </Box>
            </Paper>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <ListPageHeader
        title="DSAR Requests"
          metrics={[
            { label: 'Received', value: String(stats.pending), color: 'warning.main' },
            { label: 'Processing', value: String(stats.inProgress), color: 'info.main' },
            { label: 'Completed', value: String(stats.completed), color: 'success.main' },
            { label: 'Total', value: String(stats.total), color: 'primary.main' },
          ]}
        pieChartData={pieData}
        pieChartTitle="By Type"
      />
      <Paper sx={{ mt: 2, overflow: 'hidden' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 600 }}>Reference</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Subject Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Requested</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Time Left</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {dsarItems?.map((item: DSARRecord) => (
              <TableRow
                key={item.id}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => navigate(`/dsar-requests/${item.id}/show`)}
              >
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>{item.reference}</Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 28, height: 28, fontSize: 12, bgcolor: 'primary.main' }}>
                      {item.subjectName?.charAt(0)}
                    </Avatar>
                    <Typography variant="body2">{item.subjectName}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip label={item.type} size="small" color={DSAR_TYPE_COLORS[item.type ?? ''] as ChipColor} />
                </TableCell>
                <TableCell>
                  <Chip label={item.status} size="small" color={STATUS_COLORS[item.status ?? ''] as ChipColor} />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">{item.requestedAt}</Typography>
                </TableCell>
                <TableCell>
                  <DSARSparkline requestedAt={item.requestedAt ?? ''} status={item.status ?? ''} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={dsarItems?.length ?? 0}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{ borderTop: '1px solid', borderColor: 'divider' }}
        />
        {(!dsarItems || dsarItems.length === 0) && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <PrivacyTip sx={{ fontSize: 48, color: 'grey.300', mb: 1 }} />
            <Typography color="text.secondary">No DSAR requests found</Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

const DSARRequestShowContent = ({ record }: { record?: RaRecord }) => {
  const navigate = useNavigate();
  const [now] = useState(() => Date.now());
  if (!record) return null;
  const isPending = record.status === 'Received';
  const isCompleted = record.status === 'Completed';
  const isRejected = record.status === 'Denied';

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <IconButton onClick={() => navigate('/dsar-requests')} size="small" sx={{ color: 'text.secondary' }}>
          <Close />
        </IconButton>
      </Box>
      {isPending && (
        <Alert severity="info" sx={{ mb: 2 }}>
          DSAR pending — POPIA requires response within 30 days
        </Alert>
      )}
      {isCompleted && (
        <Alert severity="success" sx={{ mb: 2 }} icon={<CheckCircle />}>
          DSAR completed — data provided to data subject
        </Alert>
      )}
      {isRejected && (
        <Alert severity="error" sx={{ mb: 2 }}>
          DSAR rejected — grounds for refusal documented
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Reference', value: record.reference, bold: true },
          { label: 'Subject Name', value: record.subjectName },
          { label: 'Type', value: record.type, chip: true },
          { label: 'Status', value: record.status, chip: true },
          { label: 'Requested At', value: record.requestedAt },
          { label: 'Response Date', value: record.responseDate || '—' },
        ].map(f => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={f.label}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">{f.label}</Typography>
              {f.chip ? (
                <Box sx={{ mt: 0.5 }}>
                  <Chip label={f.value} size="small" color={STATUS_COLORS[record.status] as ChipColor} />
                </Box>
              ) : (
                <Typography variant="body1" sx={{ fontWeight: f.bold ? 700 : 400, mt: 0.5 }}>{f.value ?? '—'}</Typography>
              )}
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>POPIA DSAR Process Timeline</Typography>
            <Stepper activeStep={DSAR_STATUS.indexOf(record.status || 'Received')} alternativeLabel>
              <Step completed={DSAR_STATUS.indexOf(record.status || 'Received') > 0}>
                <StepLabel>Received</StepLabel>
              </Step>
              <Step completed={DSAR_STATUS.indexOf(record.status || 'Received') > 1}>
                <StepLabel>Verification</StepLabel>
              </Step>
              <Step completed={DSAR_STATUS.indexOf(record.status || 'Received') > 2}>
                <StepLabel>Processing</StepLabel>
              </Step>
              <Step completed={DSAR_STATUS.indexOf(record.status || 'Received') > 3}>
                <StepLabel>Completed</StepLabel>
              </Step>
            </Stepper>
            <Alert severity="info" sx={{ mt: 2 }}>
              POPIA requires completion within 30 days of request — days elapsed: {record.requestedAt ? Math.ceil((now - new Date(record.requestedAt).getTime()) / (1000 * 60 * 60 * 24)) : 0}
            </Alert>
          </Paper>
        </Grid>
      </Grid>

      {record.description && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: 'action.hover' }}>
          <Typography variant="caption" color="text.secondary">Description</Typography>
          <Typography variant="body2">{record.description}</Typography>
        </Paper>
      )}

      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button component={Link} to="/compliance-calendar" variant="outlined" size="small" startIcon={<PrivacyTip />}>
          Compliance Calendar
        </Button>
        <Button component={Link} to="/audit-logs" variant="outlined" size="small" startIcon={<FactCheck />}>
          Audit Logs
        </Button>
      </Box>
    </Box>
  );
};

export const DSARRequestShow = () => (
  <Show render={(props) => <DSARRequestShowContent {...props} />} />
);

export const DSARRequestCreate = () => (
  <Create>
    <SimpleForm>
      <Alert severity="info" sx={{ mb: 2 }}>
        DSAR (Data Subject Access Request) per POPIA Section 23
      </Alert>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
        <TextInput source="reference" fullWidth />
        <TextInput source="subjectName" label="Subject Name" fullWidth />
        <SelectInput source="type" choices={DSAR_TYPES.map(t => ({ id: t, name: t }))} fullWidth />
        <DateField source="requestedAt" label="Requested At" />
        <SelectInput source="status" choices={DSAR_STATUS.map(s => ({ id: s, name: s }))} fullWidth defaultValue="Received" />
        <DateField source="responseDate" label="Response Date" />
      </Box>
      <TextInput source="description" multiline fullWidth />
    </SimpleForm>
  </Create>
);

export const DSARRequestEdit = () => (
  <Edit>
    <SimpleForm>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
        <TextInput source="reference" fullWidth />
        <TextInput source="subjectName" label="Subject Name" fullWidth />
        <SelectInput source="type" choices={DSAR_TYPES.map(t => ({ id: t, name: t }))} fullWidth />
        <SelectInput source="status" choices={DSAR_STATUS.map(s => ({ id: s, name: s }))} fullWidth />
        <DateField source="requestedAt" label="Requested At" />
        <DateField source="responseDate" label="Response Date" />
      </Box>
      <TextInput source="description" multiline fullWidth />
    </SimpleForm>
  </Edit>
);